<template>
  <div class="shop-pay-page">
    <nav_list :index="4"></nav_list>

    <div class="pay-wrap">
      <div v-if="loading" class="state-card">
        <i class="el-icon-loading"></i>
        <span>订单信息加载中...</span>
      </div>

      <div v-else-if="error" class="state-card state-card--error">
          <div class="state-main">
            <div class="state-title">支付页加载失败</div>
            <div class="state-desc">{{ error }}</div>
            <div class="state-actions">
              <el-button type="primary" @click="loadOrderInfo">重新加载</el-button>
            <el-button @click="jump('/shop')">返回购票页</el-button>
            </div>
          </div>
        </div>

      <template v-else-if="orderInfo">
        <div class="pay-head">
          <div class="pay-head-main">
            <div class="page-tag">票务支付</div>
            <h1 class="page-title">确认并完成购票</h1>
            <div class="page-subtitle">订单号：{{ orderInfo.sn || orderSn }}</div>
          </div>
          <div class="status-strip">
            <span class="status-strip-label">订单状态</span>
            <span class="status-pill" :class="'status-pill--' + currentStatusTheme">
              <i class="status-pill-dot"></i>
              {{ statusText }}
            </span>
          </div>
        </div>

        <div v-if="showCountdown" class="countdown-banner" :class="{ 'countdown-banner--urgent': isUrgent }">
          <span>请在</span>
          <strong>{{ countdownMinutes }}:{{ countdownSeconds }}</strong>
          <span>内完成支付，超时后订单将失效。</span>
        </div>

        <div v-else-if="showExpired" class="countdown-banner countdown-banner--expired">
          <span>该订单已超时，请返回商品页重新下单。</span>
        </div>

        <div class="pay-layout">
          <div class="pay-main">
            <section class="pay-card hero-card">
              <div class="hero-top">
                <div class="hero-cover">
                  <img :src="productImage" alt="">
                </div>
                <div class="hero-content">
                  <div class="hero-eyebrow">票务信息</div>
                  <h2 class="hero-title">{{ productTitle }}</h2>
                  <div class="hero-package">{{ packageTitle }}</div>
                  <div class="hero-meta">
                    <span v-if="shouldShowServiceDate()">服务日期：{{ formatDisplayDate(orderInfo.s_date) }}<template v-if="orderInfo.e_date && orderInfo.e_date !== orderInfo.s_date"> 至 {{ formatDisplayDate(orderInfo.e_date) }}</template></span>
                    <span>下单时间：{{ formatDateTime(orderInfo.created_at) || '-' }}</span>
                    <span>数量：{{ orderQuantity }} {{ orderUnit || '份' }}</span>
                  </div>
                </div>
              </div>
            </section>

            <section class="pay-card" v-if="buyerInfo">
              <div class="section-title">买家信息</div>
              <div class="info-grid">
                <div class="info-item">
                  <span class="info-label">姓名</span>
                  <strong class="info-value">{{ buyerName || '-' }}</strong>
                </div>
                <div class="info-item">
                  <span class="info-label">联系电话</span>
                  <strong class="info-value">{{ buyerInfo.buyer_tel_number || '-' }}</strong>
                </div>
                <div class="info-item">
                  <span class="info-label">邮箱</span>
                  <strong class="info-value">{{ buyerInfo.buyer_Email || '-' }}</strong>
                </div>
              </div>
            </section>

            <section class="pay-card" v-if="travelers.length">
              <div class="section-title">出行信息</div>
              <div class="traveler-list">
                <div v-for="(traveler, index) in travelers" :key="'traveler-' + index" class="traveler-card">
                  <div class="traveler-title">{{ getTravelerTitle(traveler, index) }}</div>
                  <div class="traveler-lines">
                    <div
                      v-for="line in getTravelerDisplayLines(traveler)"
                      :key="line.label + '-' + line.value"
                      class="traveler-line"
                    >
                      <span>{{ line.label }}</span>
                      <strong>{{ line.value }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section class="pay-card" v-if="refundPolicyLines.length">
              <div class="section-title">退款政策</div>
              <ul class="policy-list">
                <li v-for="(item, index) in refundPolicyLines" :key="'refund-' + index">
                  {{ item }}
                </li>
              </ul>
            </section>

            <section class="pay-card" v-if="orderNote">
              <div class="section-title">订单备注</div>
              <div class="note-box">{{ orderNote }}</div>
            </section>
          </div>

          <aside class="pay-side">
            <div class="side-card">
              <div class="side-head">
                <span>待支付金额</span>
                <strong>¥ {{ orderAmountText }}</strong>
              </div>

              <div class="pay-methods" v-if="!isPaid">
                <div class="method-title">支付方式</div>
                <label class="method-option" :class="{ 'method-option--active': payType === 'wxpay' }">
                  <input v-model="payType" type="radio" name="pay_type" value="wxpay">
                  <div class="method-icon method-icon--wx">微</div>
                  <div class="method-copy">
                    <strong>微信支付</strong>
                    <span>适合扫码支付</span>
                  </div>
                </label>
                <label class="method-option" :class="{ 'method-option--active': payType === 'alipay' }">
                  <input v-model="payType" type="radio" name="pay_type" value="alipay">
                  <div class="method-icon method-icon--ali">支</div>
                  <div class="method-copy">
                    <strong>支付宝</strong>
                    <span>跳转完成支付</span>
                  </div>
                </label>
              </div>

              <div class="side-tips">
                <div>订单号：{{ orderInfo.sn || orderSn }}</div>
                <div v-if="showExpired">当前订单已失效</div>
                <div v-else-if="isPaid">支付已完成，可继续预订其他景点门票。</div>
                <div v-else>支付完成后页面会自动刷新状态。</div>
              </div>

              <el-button
                v-if="!isPaid"
                type="primary"
                class="pay-btn"
                :loading="paying"
                :disabled="showExpired"
                @click="confirmPay"
              >
                {{ showExpired ? '订单已超时' : (paying ? '支付处理中...' : '立即支付') }}
              </el-button>

              <el-button v-else type="success" class="pay-btn" @click="jump('/shop')">
                继续预订
              </el-button>


            </div>
          </aside>
        </div>
      </template>
    </div>

    <el-dialog
      title="微信支付"
      :visible.sync="qrDialogVisible"
      width="420px"
      @close="stopPayStatusPolling"
    >
      <div class="qr-dialog">
        <div class="qr-wrap">
          <div ref="qrCodeUrl" class="qrcode"></div>
        </div>
        <div class="qr-text">请使用微信扫一扫完成支付</div>
        <div class="qr-subtext">支付成功后页面会自动刷新</div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import QRCode from 'qrcodejs2';
import nav_list from '../index/nav_list.vue';

export default {
  name: 'shop-pay',
  components: {
    nav_list,
  },
  data() {
    return {
      loading: false,
      paying: false,
      error: '',
      orderSn: '',
      sourceFrom: '',
      orderInfo: null,
      orderExtra: null,
      payType: 'wxpay',
      qrDialogVisible: false,
      remainingSeconds: 0,
      showCountdown: false,
      showExpired: false,
      countdownTimer: null,
      payStatusTimer: null,
    };
  },
  computed: {
    buyerInfo() {
      return this.orderExtra || null;
    },
    travelers() {
      return this.orderExtra && this.orderExtra.custom ? this.orderExtra.custom : [];
    },
    buyerName() {
      if (!this.buyerInfo) return '';
      return [this.buyerInfo.buyer_last_name, this.buyerInfo.buyer_first_name].filter(Boolean).join('');
    },
    orderAmountText() {
      const value = this.orderInfo && this.orderInfo.b2c_total_price ? this.orderInfo.b2c_total_price : 0;
      return this.formatMoney(value);
    },
    productTitle() {
      return this.orderInfo && this.orderInfo.product && this.orderInfo.product.prod_name
        ? this.orderInfo.product.prod_name
        : '商品订单';
    },
    packageTitle() {
      if (this.orderInfo && this.orderInfo.package && this.orderInfo.package.pkg_name) {
        return this.orderInfo.package.pkg_name;
      }
      if (this.orderExtra && this.orderExtra.pkg_name) {
        return this.orderExtra.pkg_name;
      }
      return '套餐信息';
    },
    productImage() {
      if (this.orderInfo && this.orderInfo.product && this.orderInfo.product.img_list && this.orderInfo.product.img_list.length) {
        return this.orderInfo.product.img_list[0];
      }
      return require('../../../static/image/cover1.png');
    },
    orderQuantity() {
      if (this.orderInfo && this.orderInfo.num) return this.orderInfo.num;
      const skus = this.orderExtra && this.orderExtra.skus ? this.orderExtra.skus : [];
      if (!skus.length) return 1;
      return skus.reduce((sum, item) => sum + Number(item.qty || 0), 0);
    },
    orderUnit() {
      return this.orderExtra && this.orderExtra.unit ? this.orderExtra.unit : '';
    },
    statusText() {
      const map = {
        pending: '待付款',
        paid: '已付款',
        send: '已发货',
        cancelled: '已取消',
        refunded: '已退款',
      };
      return map[this.orderInfo && this.orderInfo.order_status] || (this.orderInfo && this.orderInfo.order_status) || '未知状态';
    },
    currentStatusTheme() {
      if (!this.orderInfo || !this.orderInfo.order_status) return 'default';
      if (this.orderInfo.order_status === 'paid' || this.orderInfo.order_status === 'send') return 'success';
      if (this.orderInfo.order_status === 'pending') return 'warning';
      if (this.orderInfo.order_status === 'cancelled' || this.orderInfo.order_status === 'refunded') return 'muted';
      return 'default';
    },
    isPaid() {
      return !!(this.orderInfo && this.orderInfo.order_status && this.orderInfo.order_status !== 'pending');
    },
    countdownMinutes() {
      const minutes = Math.floor(this.remainingSeconds / 60);
      return minutes < 10 ? '0' + minutes : String(minutes);
    },
    countdownSeconds() {
      const seconds = this.remainingSeconds % 60;
      return seconds < 10 ? '0' + seconds : String(seconds);
    },
    isUrgent() {
      return this.remainingSeconds <= 180 && this.remainingSeconds > 0;
    },
    refundPolicyLines() {
      const props = this.getPmdlProperties(this.getOrderRefundPolicy());
      if (props && props.partial_refund && props.partial_refund.list && props.partial_refund.list.length) {
        return props.partial_refund.list.map((item) => item.desc || '').filter(Boolean);
      }
      if (props && props.policy_type && props.policy_type.desc) {
        return [props.policy_type.desc];
      }
      const detail = this.getOrderRefundDetail();
      if (detail && detail.partial_refund && detail.partial_refund.list && detail.partial_refund.list.length) {
        return detail.partial_refund.list.map((item) => item.desc || '').filter(Boolean);
      }
      return [];
    },
    orderNote() {
      return this.orderExtra && this.orderExtra.order_note ? this.orderExtra.order_note : '';
    },
  },
  mounted() {
    this.orderSn = this.$route.query.sn || this.$route.query.orderNo || this.cache('kkday_last_order_sn') || '';
    this.sourceFrom = this.$route.query.from || '';
    if (!this.orderSn) {
      this.error = '缺少订单号';
      return;
    }
    this.loadOrderInfo();
  },
  beforeDestroy() {
    this.clearCountdownTimer();
    this.stopPayStatusPolling();
  },
  methods: {
    async loadOrderInfo(silent) {
      if (!this.orderSn) return;
      if (!silent) {
        this.loading = true;
        this.error = '';
      }
      try {
        const response = await this.get('/kkday/orders/' + this.orderSn, {}, 'get', false);
        if (!response || !response.base) {
          if (!silent) {
            this.error = '订单信息获取失败';
          }
          return;
        }
        this.orderInfo = response.base;
        this.orderExtra = response.extra || {};
        if (this.orderInfo && this.orderInfo.b2c_total_price) {
          this.cache('kkday_last_order_sn', this.orderInfo.sn || this.orderSn);
        }
        if (this.isPaid) {
          this.clearCountdownTimer();
          this.stopPayStatusPolling();
          this.qrDialogVisible = false;
          if (this.shouldRedirectToSuccess()) {
            this.goPaySuccess();
            return;
          }
          return;
        }
        this.startCountdown();
      } catch (error) {
        if (!silent) {
          this.error = '网络错误，请稍后重试';
        }
      } finally {
        if (!silent) {
          this.loading = false;
        }
      }
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2);
    },
    formatDateTime(value) {
      if (!value) return '';
      return String(value).replace('T', ' ').slice(0, 16);
    },
    formatDisplayDate(value) {
      if (!value) return '-';
      return String(value).slice(0, 10);
    },
    normalizeRefundDetail(detail) {
      if (!detail) return null;
      if (typeof detail === 'string') {
        try {
          return JSON.parse(detail);
        } catch (error) {
          return null;
        }
      }
      return detail;
    },
    getOrderRefundDetail() {
      if (!this.orderInfo) return null;
      return this.normalizeRefundDetail(this.orderInfo.package_snapshot)
        || this.normalizeRefundDetail(this.orderInfo.package)
        || null;
    },
    getOrderRefundPolicy() {
      const detail = this.getOrderRefundDetail();
      return detail && detail.description_module && detail.description_module.PMDL_REFUND_POLICY
        ? detail.description_module.PMDL_REFUND_POLICY
        : this.orderInfo && this.orderInfo.extra && this.orderInfo.extra.pkg_refund_policy
          ? this.orderInfo.extra.pkg_refund_policy
          : this.orderExtra && this.orderExtra.pkg_refund_policy
            ? this.orderExtra.pkg_refund_policy
            : null;
    },
    getPmdlProperties(moduleData) {
      if (!moduleData) return null;
      if (moduleData.content && moduleData.content.properties) return moduleData.content.properties;
      if (moduleData.properties) return moduleData.properties;
      return moduleData;
    },
    shouldShowServiceDate() {
      if (!this.orderInfo) return false;
      if (this.orderInfo.sts && this.orderInfo.sts.go_date_setting && this.orderInfo.sts.go_date_setting.type === '04') {
        return false;
      }
      if (this.orderInfo.extra && this.orderInfo.extra.extra && this.orderInfo.extra.extra.go_date_setting
        && this.orderInfo.extra.extra.go_date_setting.type === '04') {
        return false;
      }
      if (this.orderInfo.extra && this.orderInfo.extra.go_date_setting && this.orderInfo.extra.go_date_setting.type === '04') {
        return false;
      }
      if (this.orderExtra && this.orderExtra.go_date_setting && this.orderExtra.go_date_setting.type === '04') {
        return false;
      }
      if (!this.orderInfo.s_date) return false;
      return ['0000-00-00', '0000-00-00 00:00:00', ''].indexOf(this.orderInfo.s_date) === -1;
    },
    getTravelerTitle(traveler, index) {
      if (traveler.cus_type === 'contact') return '联系人';
      if (traveler.cus_type === 'send') return '寄送信息';
      if (traveler.cus_type === 'cus_01') return '旅客代表';
      return '旅客 ' + (index + 1);
    },
    getTravelerDisplayLines(traveler) {
      const lines = [];
      const pushLine = (label, value) => {
        if (!value) return;
        lines.push({ label, value });
      };
      pushLine('本地姓名', [traveler.native_last_name, traveler.native_first_name].filter(Boolean).join(''));
      pushLine('英文姓名', [traveler.english_first_name, traveler.english_last_name].filter(Boolean).join(' '));
      pushLine('护照号码', traveler.passport_no);
      pushLine('出生日期', traveler.birth);
      pushLine('联系电话', traveler.tel_number);
      pushLine('国籍', traveler.nationality);
      pushLine('地址', traveler.address);
      pushLine('酒店名称', traveler.hotel_name);
      return lines;
    },
    startCountdown() {
      this.clearCountdownTimer();
      if (!this.orderInfo || !this.orderInfo.created_at) return;
      const createdAtStr = String(this.orderInfo.created_at).replace(' ', 'T') + '+08:00';
      const createdAt = Date.parse(createdAtStr);
      const expireSeconds = 15 * 60;
      const now = Date.now();
      const elapsed = Math.floor((now - createdAt) / 1000);
      const remaining = expireSeconds - elapsed;
      if (remaining <= 0) {
        this.showCountdown = false;
        this.showExpired = true;
        return;
      }
      this.remainingSeconds = remaining;
      this.showCountdown = true;
      this.showExpired = false;
      this.countdownTimer = setInterval(() => {
        this.remainingSeconds -= 1;
        if (this.remainingSeconds <= 0) {
          this.clearCountdownTimer();
          this.showCountdown = false;
          this.showExpired = true;
        }
      }, 1000);
    },
    clearCountdownTimer() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    startPayStatusPolling() {
      this.stopPayStatusPolling();
      this.payStatusTimer = setInterval(async () => {
        await this.loadOrderInfo(true);
      }, 3000);
    },
    stopPayStatusPolling() {
      if (this.payStatusTimer) {
        clearInterval(this.payStatusTimer);
        this.payStatusTimer = null;
      }
    },
    rememberPayRedirectFlag() {
      this.cache('kkday_pay_success_pending', {
        sn: this.orderSn,
        time: Date.now(),
      });
    },
    clearPayRedirectFlag() {
      this.cache('kkday_pay_success_pending', null);
    },
    shouldRedirectToSuccess() {
      if (this.sourceFrom === 'checkout') return true;
      const pending = this.cache('kkday_pay_success_pending');
      if (!pending || pending.sn !== this.orderSn) return false;
      if (!pending.time || Date.now() - pending.time > 30 * 60 * 1000) {
        this.clearPayRedirectFlag();
        return false;
      }
      return true;
    },
    goPaySuccess() {
      this.clearPayRedirectFlag();
      this.jump('/shop/order');
    },
    renderQrCode(text) {
      if (!this.$refs.qrCodeUrl) return;
      this.$refs.qrCodeUrl.innerHTML = '';
      new QRCode(this.$refs.qrCodeUrl, {
        text,
        width: 230,
        height: 230,
        colorLight: '#ffffff',
        colorDark: '#111827',
        correctLevel: QRCode.CorrectLevel.H,
      });
    },
    async confirmPay() {
      if (this.showExpired) {
        this.$message.warning('订单已超时，请重新下单');
        return;
      }
      this.paying = true;
      try {
        const response = await this.get('/kkday/orders/pay', {
          sn: this.orderSn,
          pay_type: this.payType,
        }, 'post', false);

        if (response && response.token) {
          this.cache('info', response.info);
          this.cache('token', response.token);
        }

        if (this.payType === 'alipay') {
          const payUrl = response && (response.pay_url || response.mweb_url);
          if (payUrl) {
            this.rememberPayRedirectFlag();
            window.location.href = payUrl;
            return;
          }
          if (response && response.success) {
            this.$message.success('支付成功');
            this.goPaySuccess();
            return;
          }
        }

        if (this.payType === 'wxpay') {
          const qrText = response && (response.code_url || response.code || response.mweb_url || response.pay_url);
          if (qrText) {
            this.rememberPayRedirectFlag();
            this.qrDialogVisible = true;
            this.$nextTick(() => {
              this.renderQrCode(qrText);
            });
            this.startPayStatusPolling();
            return;
          }
        }

        if (response && response.success) {
          this.$message.success('支付成功');
          this.goPaySuccess();
          return;
        }

        this.$message.warning('支付配置异常，请稍后重试');
      } catch (error) {
        this.$message.error('支付失败，请重试');
      } finally {
        this.paying = false;
      }
    },
  },
};
</script>

<style scoped>
.shop-pay-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at top right, rgba(37, 99, 235, 0.09), transparent 30%),
    linear-gradient(180deg, #f5f7fb 0%, #edf2f9 100%);
  padding-bottom: 48px;
}

.pay-wrap {
  width: 1200px;
  margin: 24px auto 0;
}

.state-card,
.pay-card,
.side-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 18px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.06);
}

.state-card {
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
  font-size: 16px;
}

.state-main {
  text-align: center;
}

.state-title {
  color: #0f172a;
  font-size: 24px;
  font-weight: 700;
}

.state-desc {
  margin-top: 12px;
  color: #64748b;
}

.state-actions {
  margin-top: 20px;
}

.pay-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 18px;
  gap: 18px;
}

.pay-head-main {
  min-width: 0;
}

.page-tag {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.1);
  color: #2563eb;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.page-title {
  margin: 14px 0 10px;
  color: #0f172a;
  font-size: 34px;
  line-height: 1.15;
}

.page-subtitle {
  color: #64748b;
  font-size: 14px;
}

.status-strip {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 8px;
}

.status-strip-label {
  color: #64748b;
  font-size: 13px;
  white-space: nowrap;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

.status-pill-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}

.status-pill--success {
  color: #15803d;
  background: #ecfdf5;
}

.status-pill--warning {
  color: #c2410c;
  background: #fff7ed;
}

.status-pill--muted {
  color: #475569;
  background: #f1f5f9;
}

.status-pill--default {
  color: #2563eb;
  background: #eff6ff;
}

.countdown-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  padding: 16px 20px;
  border-radius: 16px;
  background: linear-gradient(90deg, #fff7ed 0%, #fffbeb 100%);
  color: #9a3412;
  font-size: 15px;
}

.countdown-banner strong {
  font-size: 24px;
}

.countdown-banner--urgent {
  background: linear-gradient(90deg, #fef2f2 0%, #fff7ed 100%);
  color: #b91c1c;
}

.countdown-banner--expired {
  background: #fee2e2;
  color: #991b1b;
}

.pay-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 24px;
}

.pay-main {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.pay-side {
  position: relative;
}

.side-card {
  position: sticky;
  top: 96px;
  padding: 24px;
}

.hero-card,
.pay-card {
  padding: 24px;
}

.hero-top {
  display: flex;
  gap: 22px;
}

.hero-cover {
  width: 220px;
  height: 148px;
  border-radius: 16px;
  overflow: hidden;
  background: #e2e8f0;
  flex-shrink: 0;
}

.hero-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-eyebrow {
  color: #2563eb;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.hero-title {
  margin: 12px 0 8px;
  color: #0f172a;
  font-size: 28px;
  line-height: 1.3;
}

.hero-package {
  color: #334155;
  font-size: 16px;
  font-weight: 600;
}

.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  margin-top: 16px;
  color: #64748b;
  font-size: 14px;
}

.section-title {
  margin-bottom: 18px;
  color: #0f172a;
  font-size: 20px;
  font-weight: 700;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.info-item {
  padding: 16px 18px;
  border-radius: 14px;
  background: #f8fafc;
}

.info-label {
  display: block;
  color: #64748b;
  font-size: 12px;
}

.info-value {
  display: block;
  margin-top: 8px;
  color: #0f172a;
  font-size: 16px;
}

.traveler-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.traveler-card {
  padding: 18px;
  border-radius: 16px;
  background: #f8fafc;
}

.traveler-title {
  color: #0f172a;
  font-size: 16px;
  font-weight: 700;
}

.traveler-lines {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 18px;
  margin-top: 14px;
}

.traveler-line {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  color: #475569;
}

.traveler-line strong {
  color: #0f172a;
  text-align: right;
}

.policy-list {
  margin: 0;
  padding-left: 18px;
  color: #475569;
  line-height: 1.85;
}

.note-box {
  padding: 16px 18px;
  border-radius: 14px;
  background: #f8fafc;
  color: #475569;
  line-height: 1.8;
  white-space: pre-wrap;
}

.side-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 20px;
  color: #0f172a;
}

.side-head span {
  color: #64748b;
  font-size: 14px;
}

.side-head strong {
  font-size: 28px;
}

.pay-methods {
  padding: 18px;
  border-radius: 16px;
  background: #f8fafc;
}

.method-title {
  margin-bottom: 12px;
  color: #0f172a;
  font-size: 14px;
  font-weight: 700;
}

.method-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #dbeafe;
  border-radius: 14px;
  background: #fff;
  cursor: pointer;
}

.method-option + .method-option {
  margin-top: 12px;
}

.method-option input {
  margin: 0;
}

.method-option--active {
  border-color: #2563eb;
  box-shadow: inset 0 0 0 1px rgba(37, 99, 235, 0.2);
}

.method-icon {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
}

.method-icon--wx {
  background: linear-gradient(135deg, #16a34a, #22c55e);
}

.method-icon--ali {
  background: linear-gradient(135deg, #2563eb, #38bdf8);
}

.method-copy {
  display: flex;
  flex-direction: column;
}

.method-copy strong {
  color: #0f172a;
  font-size: 15px;
}

.method-copy span {
  color: #64748b;
  font-size: 12px;
  margin-top: 4px;
}

.side-tips {
  margin: 18px 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.8;
}

.pay-btn,
.ghost-btn {
  width: 100%;
}

.ghost-btn {
  margin-top: 12px;
}

.qr-dialog {
  text-align: center;
}

.qr-wrap {
  display: flex;
  justify-content: center;
}

.qrcode {
  width: 230px;
  min-height: 230px;
}

.qr-text {
  margin-top: 14px;
  color: #0f172a;
  font-size: 16px;
  font-weight: 700;
}

.qr-subtext {
  margin-top: 8px;
  color: #64748b;
  font-size: 13px;
}

@media (max-width: 1280px) {
  .pay-wrap {
    width: calc(100% - 40px);
  }
}

@media (max-width: 1024px) {
  .pay-layout {
    grid-template-columns: 1fr;
  }

  .side-card {
    position: static;
  }

  .info-grid,
  .traveler-lines {
    grid-template-columns: 1fr;
  }

  .hero-top {
    flex-direction: column;
  }

  .hero-cover {
    width: 100%;
    height: 220px;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/shop/pay.vue